import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface KYC {
  _id: string;
  name: string;
  code: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

const statusOptions = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

const KYCManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingKYC, setEditingKYC] = useState<KYC | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch KYC records with server-side filtering and pagination
  const { data: kycResponse, isLoading } = useQuery({
    queryKey: ['kyc', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/kyc?${params.toString()}`);
      const result = response.data.data || {};
      console.log('KYC API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
  });

  const kycRecords = kycResponse?.data || [];
  const pagination = kycResponse?.pagination;

  // Create KYC mutation
  const createKYCMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/kyc', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'kyc'] });
      toast({
        title: 'KYC created',
        description: 'KYC record has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingKYC(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating KYC',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to create KYC record',
          variant: 'destructive',
        });
      }
    },
  });

  // Update KYC mutation
  const updateKYCMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/kyc/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'kyc'] });
      toast({
        title: 'KYC updated',
        description: 'KYC record has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingKYC(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating KYC',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to update KYC record',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete KYC mutation
  const deleteKYCMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/api/master-data/kyc/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'kyc'] });
      toast({
        title: 'KYC deleted',
        description: 'KYC record has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting KYC',
        description: error.response?.data?.error || 'Failed to delete KYC record',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<KYC>[] = [
    {
      key: 'name',
      label: 'KYC Name',
      sortable: true,
      render: (value, kyc) => (
        <div className="space-y-1">
          <div className="font-medium text-sm">
            {kyc.name}
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {kyc.code}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, kyc) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {kyc.code}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, kyc) => (
        <Badge 
          variant={kyc.status ? 'default' : 'secondary'}
          className={kyc.status 
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {kyc.status ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, kyc) => (
        <div className="text-sm text-muted-foreground">
          {new Date(kyc.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      hideOnTablet: true,
      render: (value, kyc) => (
        <div className="text-sm text-muted-foreground">
          {new Date(kyc.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      ),
    },
  ];

  // Filter configuration with improved options
  const filterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Filter by status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'true', label: 'Active KYC Requirements' },
        { value: 'false', label: 'Inactive KYC Requirements' },
      ],
    },
    {
      key: 'name',
      label: 'KYC Type',
      type: 'text',
      placeholder: 'Search by KYC name...',
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      placeholder: 'Filter by KYC code...',
    },
  ];

  // Add date filter for created date if needed
  const enhancedFilterConfig = [
    ...filterConfig,
    // Uncomment if date filtering is needed
    // {
    //   key: 'createdAt',
    //   label: 'Created Date',
    //   type: 'date' as const,
    //   placeholder: 'Filter by creation date',
    // },
  ];

  // Form sections configuration with better layout
  const formSections: FormSection[] = [
    {
      title: 'KYC Information',
      description: 'Configure KYC (Know Your Customer) requirements and settings. The KYC code will be auto-generated.',
      fields: [
        {
          name: 'name',
          label: 'KYC Name',
          type: 'text',
          required: true,
          placeholder: 'Enter KYC requirement name (e.g., "Digital Passport", "PAN Card")',
          description: 'A descriptive name for this KYC requirement',
          validation: z.string()
            .min(2, 'Name must be at least 2 characters')
            .max(100, 'Name cannot exceed 100 characters')
            .regex(/^[a-zA-Z0-9\s\-_().]+$/, 'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: true, label: 'Active - Available for use' },
            { value: false, label: 'Inactive - Not available for use' }
          ],
          description: 'Set the availability status for this KYC requirement',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingKYC(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (kyc: KYC) => {
    // Transform data for form editing
    const transformedKYC = {
      ...kyc,
      // Ensure status is properly set for radio buttons
      status: kyc.status,
    };
    setEditingKYC(transformedKYC);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (kyc: KYC) => {
    if (window.confirm(`Are you sure you want to delete "${kyc.name}"?`)) {
      deleteKYCMutation.mutate(kyc._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Convert string 'true'/'false' to boolean if needed
      status: typeof data.status === 'string' ? data.status === 'true' : Boolean(data.status),
    };
    
    // Remove code field since it's auto-generated
    delete transformedData.code;

    if (editingKYC) {
      updateKYCMutation.mutate({ id: editingKYC._id, data: transformedData });
    } else {
      createKYCMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingKYC(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['kyc'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/kyc/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Status', 'Created Date', 'Updated Date'];
          const csvContent = [
            headers.join(','),
            ...data.map((kyc: any) => [
              `"${kyc.name || ''}"`,
              `"${kyc.code || ''}"`,
              `"${kyc.status ? 'Active' : 'Inactive'}"`,
              `"${new Date(kyc.createdAt).toLocaleDateString()}"`,
              `"${new Date(kyc.updatedAt).toLocaleDateString()}"`
            ].join(','))
          ].join('\n');

          // Download CSV
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `kyc_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} KYC records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No KYC records match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export KYC records',
        variant: 'destructive',
      });
    }
  };


  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="KYC Management"
          description="Manage KYC (Know Your Customer) requirements and document types"
          data={kycRecords}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          searchPlaceholder="Search KYC requirements by name, code, or type..."
          filters={enhancedFilterConfig}
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleSubmit}
          sections={formSections}
          title={editingKYC ? 'Edit KYC Requirement' : 'Add New KYC Requirement'}
          description={
            editingKYC
              ? 'Update the KYC requirement details and availability status.'
              : 'Create a new KYC requirement for customer verification processes.'
          }
          isLoading={createKYCMutation.isPending || updateKYCMutation.isPending}
          initialData={editingKYC || { status: true, name: '', code: '' }}
          mode={editingKYC ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="KYC Requirement"
        />
      </div>
    </div>
  );
};

export default KYCManagement;
